import { z } from 'zod';

import { AuthReqDeviceCoordsDto } from '~modules/auth/model';
import { JournalDto, JournalDtoScheme, JournalManualDto, JournalManualDtoSchema } from '~modules/journal/model';
import { DeviceInfoDto } from '~modules/user-device/model';
import { client } from '~shared/api';

/**
 * Зарегистрироваться по QR коду
 * @param qr
 * @param device_info признаки устройства сотрудника
 * @param coords
 */
export async function createAuto(
	qr: string,
	device_info: DeviceInfoDto,
	coords?: AuthReqDeviceCoordsDto | null,
): Promise<JournalDto> {
	const data = await client.post('/journal/auto', {
		qr,
		coords,
		device_code: device_info.client_id,
		device_info,
	});
	return JournalDtoScheme.parse(data);
}

/**
 * Завершить регистрацию по QR коду с фото
 * @param journal
 * @param file
 * @param device_info признаки устройства - проверка устройства пересчитывается на сервере
 */
export async function updateAuto(journal: JournalDto, file: File, device_info: DeviceInfoDto): Promise<JournalDto> {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('journal', JSON.stringify(journal));
	formData.append('device_info', JSON.stringify(device_info));

	const data = await client.put('/journal/auto', formData);
	return JournalDtoScheme.parse(data);
}

/**
 * Ручная регистрация пользователя бригадиром
 * @param user_id
 * @param file
 * @param is_part_time
 */
export async function manualCreate(user_id: number, file: File, is_part_time: boolean): Promise<void> {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('user_id', user_id.toString());
	formData.append('is_part_time', is_part_time ? 'true' : 'false');

	await client.post('/journal/manual', formData);
}

/**
 * Ручное завершение сессии
 * @param user_id
 * @param file
 */
export async function manualFinish(user_id: number, file: File): Promise<void> {
	const formData = new FormData();
	formData.append('user_id', user_id.toString());
	formData.append('file', file);

	await client.put(`/journal/manual`, formData);
}

/**
 * Список активных сессий
 * @param active
 */
export async function listSessions(active: boolean): Promise<JournalManualDto[]> {
	const data = await client.get(`/journal?active=${active}`);
	return z.array(JournalManualDtoSchema).parse(data);
}

export async function loadErrors(): Promise<JournalDto[]> {
	const data = await client.get('/journal/errors');
	return z.array(JournalDtoScheme).parse(data);
}

/**
 * Список записей журнала с участком, пользователем и бригадиром
 */
export async function loadJournalList(
	date_start?: Date | null,
	date_end?: Date | null,
	user_id?: number,
	limit?: number,
	offset?: number,
): Promise<{ list: JournalDto[]; total: number }> {
	const params = new URLSearchParams();

	if (date_start) params.append('date_start', date_start.toISOString());
	if (date_end) params.append('date_end', date_end.toISOString());
	if (user_id) params.append('user_id', user_id.toString());
	if (limit !== undefined) params.append('limit', limit.toString());
	if (offset !== undefined) params.append('offset', offset.toString());

	const data = await client.get(`/journal/list?${params.toString()}`);
	return z.object({ list: z.array(JournalDtoScheme), total: z.number() }).parse(data);
}
